/**
 * UX-C04 — Proactive suggestions rule registry.
 *
 * Each rule is a pure function over `ProactiveContext`. The engine evaluates
 * them on a tick and surfaces the top N suggestions in the UI.
 */

export type UIAction =
  | { type: 'navigate'; path: string }
  | { type: 'open-agent'; prompt: string }
  | { type: 'noop' };

export interface KpiSnapshot {
  oee?: number;          // 0..100
  line?: string;         // e.g. "L-01"
}

export interface NCSnapshot {
  id: string;
  status: 'open' | 'disposed' | 'closed';
  openedAt: number;      // epoch ms
}

export interface CameraSnapshot {
  id: string;
  driftPct: number;      // 0..100
}

export interface ProactiveContext {
  kpi?: KpiSnapshot;
  ncs?: NCSnapshot[];
  cameras?: CameraSnapshot[];
  now?: number;
}

export interface ProactiveRule {
  id: string;
  title: string;
  condition: (ctx: ProactiveContext) => boolean;
  suggestion: (ctx: ProactiveContext) => string;
  action: (ctx: ProactiveContext) => UIAction;
}

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

export const RULES: ProactiveRule[] = [
  {
    id: 'oee_below_threshold',
    title: 'OEE bajo umbral',
    condition: (ctx) => typeof ctx.kpi?.oee === 'number' && ctx.kpi!.oee < 70,
    suggestion: (ctx) =>
      `Revisar bottleneck en línea ${ctx.kpi?.line ?? 'L-01'} (OEE ${ctx.kpi?.oee?.toFixed(1) ?? '?'}%).`,
    action: (ctx) => ({
      type: 'open-agent',
      prompt: `Analiza el bottleneck actual de la línea ${ctx.kpi?.line ?? 'L-01'} y propón 3 acciones priorizadas.`,
    }),
  },
  {
    id: 'nc_undisposed_2h',
    title: 'NC sin disponer',
    condition: (ctx) => {
      const now = ctx.now ?? Date.now();
      return (ctx.ncs ?? []).some(
        (nc) => nc.status === 'open' && now - nc.openedAt > TWO_HOURS_MS,
      );
    },
    suggestion: (ctx) => {
      const now = ctx.now ?? Date.now();
      const nc = (ctx.ncs ?? []).find(
        (n) => n.status === 'open' && now - n.openedAt > TWO_HOURS_MS,
      );
      return `Disponer NC ${nc?.id ?? ''} (abierta > 2h sin acción).`;
    },
    action: () => ({ type: 'navigate', path: '/quality/nc-board' }),
  },
  {
    id: 'camera_drift',
    title: 'Drift de cámara',
    condition: (ctx) => (ctx.cameras ?? []).some((c) => c.driftPct > 5),
    suggestion: (ctx) => {
      const cam = (ctx.cameras ?? []).find((c) => c.driftPct > 5);
      return `Recalibrar cámara ${cam?.id ?? ''} (drift ${cam?.driftPct.toFixed(1)}%).`;
    },
    action: (ctx) => {
      const cam = (ctx.cameras ?? []).find((c) => c.driftPct > 5);
      return { type: 'navigate', path: `/vision/cameras/${cam?.id ?? ''}` };
    },
  },
];
